import { IconButton as MuiIconButton, Tooltip } from '@mui/material';
import { PIconButtonProps } from '@/components/atoms/IconButton/types';
import { restrictedSxPropertiesError } from '@/components/atoms/IconButton/utils';
import { mergeSx } from '@/utils/cssStyles';

export const PIconButton = ({
  icon,
  'aria-label': ariaLabel,
  size = 'medium',
  variant = 'outlined',
  disabled,
  onClick,
  type,
  sx,
  tooltip,
}: PIconButtonProps) => {
  restrictedSxPropertiesError(sx);

  const button = (
    <MuiIconButton
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      size={size}
      type={type}
      sx={mergeSx((theme) => {
        switch (variant) {
          case 'contained':
            return {
              borderRadius: 1,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              aspectRatio: '1 / 1',
              '&:hover': { backgroundColor: theme.palette.primary.dark },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            };
          case 'outlined':
            return {
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              color: theme.palette.text.primary,
              aspectRatio: '1 / 1',
              '&.Mui-disabled': { color: theme.palette.action.disabled },
            };
          case 'ghost':
            return {
              // Circular, to match the hover background table rows and context
              // menus already use; the square radius of the other two variants
              // reads as a control, which is what a clean-at-rest header is not.
              borderRadius: '50%',
              color: theme.palette.text.secondary,
              aspectRatio: '1 / 1',
              '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
              '&.Mui-disabled': { color: theme.palette.action.disabled },
            };
          default: {
            const exhaustiveCheck: never = variant;
            throw new Error(`Unhandled icon button variant: ${exhaustiveCheck}`);
          }
        }
      }, sx)}
    >
      {icon}
    </MuiIconButton>
  );

  const tooltipText = tooltip ?? ariaLabel;
  if (tooltipText === '') return button;

  // Tooltip does not fire on disabled children — wrap in a span when disabled
  // so the listener has a non-disabled element to attach to.
  return <Tooltip title={tooltipText}>{disabled ? <span>{button}</span> : button}</Tooltip>;
};
